/**
 * OutreachEditor — review, edit, regenerate, and copy the AI-drafted outreach.
 * The AI is ADVISORY: there is no "send" — the rep copies the edited text and
 * sends it themselves. Rationale + source attributions are shown alongside.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Recommendation } from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

export function OutreachEditor(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [rec, setRec] = useState<Recommendation | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const applyRec = (r: Recommendation | null): void => {
    setRec(r);
    setDraft(r?.editedDraft ?? r?.outreachDraft ?? '');
  };

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.getRecommendation(id);
      applyRec(res.recommendation);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const onRegenerate = async (): Promise<void> => {
    if (!id) return;
    setBusy('regenerate');
    setError(null);
    setNotice(null);
    try {
      const res = await api.regenerateRecommendation(id);
      applyRec(res.recommendation);
      setNotice('Regenerated a fresh draft.');
    } catch (e) {
      setError(
        e instanceof ApiClientError ? e.message : 'Could not generate a draft',
      );
    } finally {
      setBusy(null);
    }
  };

  const onSave = async (): Promise<void> => {
    if (!id || !rec) return;
    setBusy('save');
    setError(null);
    setNotice(null);
    try {
      const res = await api.editRecommendation(id, rec.id, draft);
      applyRec(res.recommendation);
      setNotice('Saved your edit.');
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to save');
    } finally {
      setBusy(null);
    }
  };

  const onCopy = async (): Promise<void> => {
    await navigator.clipboard.writeText(draft);
    setNotice('Copied to clipboard.');
  };

  if (loading) return <div className="centered muted">Loading…</div>;

  return (
    <div className="outreach-editor">
      <div className="page-head">
        <div>
          <Link to={`/leads/${id}`} className="back-link">
            ← Profile
          </Link>
          <h1>Outreach editor</h1>
        </div>
        <button
          type="button"
          className="btn"
          onClick={onRegenerate}
          disabled={busy !== null}
        >
          {busy === 'regenerate' ? 'Generating…' : 'Regenerate'}
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}
      {notice && <div className="form-notice">{notice}</div>}

      {!rec ? (
        <div className="card">
          <p className="muted">
            No recommendation yet for this lead. Generate one to begin.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRegenerate}
            disabled={busy !== null}
          >
            {busy === 'regenerate' ? 'Generating…' : 'Generate recommendation'}
          </button>
        </div>
      ) : (
        <>
          <section className="card">
            <h3>Engagement summary</h3>
            <p>{rec.engagementSummary}</p>
            <h3>Follow-up angle</h3>
            <p>{rec.followUpAngle}</p>
            <p className="rationale muted">
              <strong>Why this was suggested:</strong> {rec.rationale}
            </p>
          </section>

          <section className="card">
            <div className="section-head">
              <h3>Editable outreach draft</h3>
              <span className={`pill pill-${rec.status}`}>{rec.status}</span>
            </div>
            <textarea
              className="outreach-textarea"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={10}
            />
            <div className="editor-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onSave}
                disabled={busy !== null}
              >
                {busy === 'save' ? 'Saving…' : 'Save edit'}
              </button>
              <button type="button" className="btn" onClick={onCopy}>
                Copy
              </button>
            </div>
            <p className="advisory-note muted">
              Aurora never sends this. Copy the text and send it yourself.
              {rec.model && ` · Drafted by ${rec.model}`}
            </p>
          </section>
        </>
      )}
    </div>
  );
}
